import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import './BehavioralTracking.css';
import axios from 'axios';
import ClipLoader from 'react-spinners/ClipLoader';
import CONFIG from '../config.js';

const BehavioralTracking = () => {
    const { user, isAdmin, adminSelectedUser } = useContext(AuthContext);
    let userInfo = user;
    if (isAdmin) {
        userInfo = adminSelectedUser;
    }
    
    const formatDate = (date) => {

        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2)
            month = '0' + month;
        if (day.length < 2)
            day = '0' + day;

        return [year, month, day].join('-');
    }

    const url = CONFIG.createUrl();

    const config = {headers: CONFIG.HEADERS};

    const [selectedItems, setSelectedItems] = useState([]);
    const [showPreferences, setShowPreferences] = useState(false);
    const [behavioralTrackingDate, setBehavioralTrackingDate] = useState(formatDate(new Date()));
    const [showMinPreferencesError, setShowMinPreferencesError] = useState(false);
    const [selectedPreferences, setSelectedPreferences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inputValues, setInputValues] = useState({});
    const [updatePref, setUpdatePref] = useState(false);
    const [behavioralTrackingData, setBehavioralTrackingData] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [openSections, setOpenSections] = useState([]);
    const [showError, setShowError] = useState(false);
    const customMetricOptionsPayload = {
        "email": "",
        "beer_consumption": false,
        "wine_consumption": false,
        "hard_liquor_consumption": false,
        "caffeine_consumption": false,
        "sunlight_exposure_duration": false,
        "sunlight_exposure_timing": false,
        "time_with_close_family_friends": false,
        "extroverted_time": false,
        "introverted_alone_time": false,
        "bedtime_routine_implemented": false,
        "nap_frequency": false, 
        "nap_duration": false,
        "hydration_levels": false,
        "general_stress_levels": false,
        "meditation_mindfulness_duration": false,
        "breathing_exercises_frequency": false,
        "screen_time_before_bed": false,
        "social_media_use_time": false,
        "work_hours_per_day": false,
        "time_spent_outdoors": false,
        "travel_frequency": false,
        "altitude_exposure": false,
        "time_spent_in_nature": false,
        "cold_exposure_therapy": false,
        "sauna_heat_exposure_therapy": false,
        "sexual_activity": false,
        "time_spent_on_hobbies": false,
        "music_exposure_duration": false,
        "time_spent_in_silence": false,
        "supplements_taken": false,
        "emotional_well_being": false,
        "time_spent_reading_learning": false,
        "jetlag_travel_fatigue": false,
        "cold_illness": false,
        "new_supplement": false,
        "new_medicine": false,
        "market_event": false,
        "work_stress": false,
        "interpersonal_stress": false,
        "financial_stress": false
    };

    const customMetricValuesPayload = {
        "day": "",
        "email": "",
        "beer_consumption": null,
        "wine_consumption": null,
        "hard_liquor_consumption": null,
        "caffeine_consumption": null,
        "exercise_intensity": null,
        "sunlight_exposure_duration": null,
        "sunlight_exposure_timing": null,
        "time_with_close_family_friends": null,
        "extroverted_time": null,
        "introverted_alone_time": null,
        "bedtime_routine_implemented": null,
        "nap_frequency": null,
        "nap_duration": null,
        "hydration_levels": null,
        "general_stress_levels": null,
        "meditation_mindfulness_duration": null,
        "breathing_exercises_frequency": null,
        "screen_time_before_bed": null,
        "social_media_use_time": null,
        "work_hours_per_day": null,
        "time_spent_outdoors": null,
        "travel_frequency": null,
        "altitude_exposure": null,
        "time_spent_in_nature": null,
        "cold_exposure_therapy": null,
        "sauna_heat_exposure_therapy": null,
        "sexual_activity": null,
        "time_spent_on_hobbies": null,
        "music_exposure_duration": null,
        "time_spent_in_silence": null,
        "supplements_taken": null,
        "emotional_well_being": null,
        "time_spent_reading_learning": null,
        "jetlag_travel_fatigue": null,
        "cold_illness": null,
        "new_supplement": null,
        "new_medicine": null,
        "market_event": null,
        "work_stress": null,
        "interpersonal_stress": null,
        "financial_stress": null
    };

    const data = {
        "Diet & Consumption": [
          "beer_consumption", "wine_consumption", "hard_liquor_consumption", "caffeine_consumption",
          "hydration_levels", "supplements_taken", "new_supplement", "new_medicine"
        ],
        "Physical Activity & Environmental Exposure": [
          "exercise_intensity", "time_spent_outdoors", "time_spent_in_nature", "altitude_exposure",
          "travel_frequency", "jetlag_travel_fatigue", "cold_exposure_therapy", "sauna_heat_exposure_therapy"
        ],
        "Sleep & Rest": [
          "nap_frequency", "nap_duration", "bedtime_routine_implemented", "screen_time_before_bed",
          "cold_illness"
        ],
        "Social & Emotional Well-being": [
          "time_with_close_family_friends", "extroverted_time", "introverted_alone_time", "emotional_well_being",
          "time_spent_on_hobbies", "time_spent_reading_learning", "music_exposure_duration", "time_spent_in_silence"
        ],
        "Stress & Mental Health": [
          "general_stress_levels", "work_stress", "financial_stress", "interpersonal_stress",
          "meditation_mindfulness_duration", "breathing_exercises_frequency", "social_media_use_time", "market_event", "work_hours_per_day"
        ],
        "Environmental and Other Factors": [
          "sunlight_exposure_duration", "sunlight_exposure_timing", "sexual_activity", "time_spent_on_hobbies"
        ]
    };

    // Increment count value
    const incrementCount = (behavior) => {
        setInputValues((prevValues) => ({
        ...prevValues,
        [behavior]: prevValues[behavior] ? (prevValues[behavior] + 1) : 1,
        }));
        setHasChanges(true);
    };

    // Decrement count value
    const decrementCount = (behavior) => {
        setInputValues((prevValues) => ({
        ...prevValues,
        [behavior]: prevValues[behavior] ? Math.max(0, prevValues[behavior] - 1) : 0,
        }));
        setHasChanges(true);
    };

    const toggleAccordion = (index) => {
        if (openSections.includes(index)) {
          // If the section is already open, close it by removing the index
          setOpenSections(openSections.filter((i) => i !== index));
        } else {
          // If the section is closed, open it by adding the index
          setOpenSections([...openSections, index]);
        }
      };

      const validInput = (inputVal, format) => {
        if(inputVal === "" || inputVal === null || inputVal === undefined) {
            return true;
        }
        if(format === "hh:mm") {
            return (/^([01]\d|2[0-3]):([0-5]\d)$/.test(inputVal));
        } else if (format === "hh:mm:ss") {
            return (/^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/.test(inputVal));
        }
      }

    const behaviorComponents = {
        beer_consumption: (
            <div className="behavior-item">
                <div>Beer Consumption</div>
                <div className="count-container">
                    <div className="count-sign" onClick={() => decrementCount("beer_consumption")}><div className="count-sign-position">-</div></div>
                    <input className="behavior-input count-input" type="number" placeholder="Servings" value={inputValues.beer_consumption} onChange={(e) => handleInputChange("beer_consumption", e.target.value)}></input>
                    <div className="count-sign" onClick={() => incrementCount("beer_consumption")}><div className="count-sign-position">+</div></div>
                </div>
            </div>
        ),
        wine_consumption: (
            <div className="behavior-item">
                <div>Wine Consumption</div>
                <div className="count-container">
                    <div className="count-sign" onClick={() => decrementCount("wine_consumption")}><div className="count-sign-position">-</div></div>
                    <input className="behavior-input count-input" type="number" placeholder="Servings" value={inputValues.wine_consumption} onChange={(e) => handleInputChange("wine_consumption", e.target.value)}></input>
                    <div className="count-sign" onClick={() => incrementCount("wine_consumption")}><div className="count-sign-position">+</div></div>
                </div>
            </div>
        ),
        hard_liquor_consumption: (
            <div className="behavior-item">
                <div>Hard Liquor Consumption</div>
                <div className="count-container">
                    <div className="count-sign" onClick={() => decrementCount("hard_liquor_consumption")}><div className="count-sign-position">-</div></div>
                    <input className="behavior-input count-input" type="number" placeholder="Servings" value={inputValues.hard_liquor_consumption} onChange={(e) => handleInputChange("hard_liquor_consumption", e.target.value)}></input>
                    <div className="count-sign" onClick={() => incrementCount("hard_liquor_consumption")}><div className="count-sign-position">+</div></div>
                </div>
            </div>
        ),
        caffeine_consumption: (
            <div className="behavior-item">
                <div>Caffeine Consumption</div>
                <div className="count-container">
                    <div className="count-sign" onClick={() => decrementCount("caffeine_consumption")}><div className="count-sign-position">-</div></div>
                    <input className="behavior-input count-input" type="number" placeholder="Cups" value={inputValues.caffeine_consumption} onChange={(e) => handleInputChange("caffeine_consumption", e.target.value)}></input>
                    <div className="count-sign" onClick={() => incrementCount("caffeine_consumption")}><div className="count-sign-position">+</div></div>
                </div>
            </div>
        ),
        exercise_intensity: (
            <div className="behavior-item">
                <div>Exercise Intensity</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="NONE" checked={inputValues.exercise_intensity === 'NONE'} onChange={(e) => handleInputChange("exercise_intensity", e.target.value)} />
                        None
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="LOW" checked={inputValues.exercise_intensity === 'LOW'} onChange={(e) => handleInputChange("exercise_intensity", e.target.value)} />
                        Low
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="MEDIUM" checked={inputValues.exercise_intensity === 'MEDIUM'} onChange={(e) => handleInputChange("exercise_intensity", e.target.value)} />
                        Med
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="HIGH" checked={inputValues.exercise_intensity === 'HIGH'} onChange={(e) => handleInputChange("exercise_intensity", e.target.value)} />
                        High
                    </label>
                </div>
            </div>
        ),
        sunlight_exposure_duration: (
            <div className="behavior-item">
                <div>Sunlight Exposure Duration</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.sunlight_exposure_duration} onChange={(e) => handleInputChange("sunlight_exposure_duration", e.target.value)}></input>
                    {showError && !validInput(inputValues.sunlight_exposure_duration, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                </div>
            </div>
        ),
        sunlight_exposure_timing: (
            <div className="behavior-item">
                <div>Sunlight Exposure Timing</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm:ss" value={inputValues.sunlight_exposure_timing} onChange={(e) => handleInputChange("sunlight_exposure_timing", e.target.value)}></input>
                    {showError && !validInput(inputValues.sunlight_exposure_timing, "hh:mm:ss") && <p className="input-error-msg">Invalid input. Use hh:mm:ss (24hr) format.</p>}
                </div>
            </div>
        ),
        time_with_close_family_friends: (
            <div className="behavior-item">
                <div>Time with Close Family & Friends</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.time_with_close_family_friends} onChange={(e) => handleInputChange("time_with_close_family_friends", e.target.value)}></input>
                    {showError && !validInput(inputValues.time_with_close_family_friends, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                </div>
            </div>
        ),
        extroverted_time: (
            <div className="behavior-item">
                <div>Extroverted Time</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.extroverted_time} onChange={(e) => handleInputChange("extroverted_time", e.target.value)}></input>
                    {showError && !validInput(inputValues.extroverted_time, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                </div>
            </div>
        ),
        introverted_alone_time: (
            <div className="behavior-item">
                <div>Introverted Alone Time</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.introverted_alone_time} onChange={(e) => handleInputChange("introverted_alone_time", e.target.value)}></input>
                    {showError && !validInput(inputValues.introverted_alone_time, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                </div>
            </div>
        ),
        bedtime_routine_implemented: (
            <div className="behavior-item">
                <div>Bedtime Routine Implemented</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="true" checked={inputValues.bedtime_routine_implemented === true}  onChange={(e) => handleInputChange("bedtime_routine_implemented", e.target.value === "true")}/>
                        True
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="false" checked={inputValues.bedtime_routine_implemented === false}  onChange={(e) => handleInputChange("bedtime_routine_implemented", e.target.value === "true")}/>
                        False
                    </label>
                </div>
            </div>
        ),
        nap_frequency: (
            <div className="behavior-item">
                <div>Nap Frequency</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="number" placeholder="Frequency" value={inputValues.nap_frequency} onChange={(e) => handleInputChange("nap_frequency", e.target.value)}></input>
                </div>
            </div>
        ),
        nap_duration: (
            <div className="behavior-item">
                <div>Nap Duration</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.nap_duration} onChange={(e) => handleInputChange("nap_duration", e.target.value)}></input>
                    {showError && !validInput(inputValues.nap_duration, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                </div>
            </div>
        ),
        hydration_levels: (
            <div className="behavior-item">
                <div>Hydration Levels</div>
                <div className="count-container">
                    <div className="count-sign" onClick={() => decrementCount("hydration_levels")}><div className="count-sign-position">-</div></div>
                    <input className="behavior-input count-input" type="number" placeholder="Cups" value={inputValues.hydration_levels} onChange={(e) => handleInputChange("hydration_levels", e.target.value)}></input>
                    <div className="count-sign" onClick={() => incrementCount("hydration_levels")}><div className="count-sign-position">+</div></div>
                </div>
            </div>
        ),
        general_stress_levels: (
            <div className="behavior-item">
                <div>General Stress Levels</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="NONE" checked={inputValues.general_stress_levels === 'NONE'} onChange={(e) => handleInputChange("general_stress_levels", e.target.value)} />
                        None
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="LOW" checked={inputValues.general_stress_levels === 'LOW'} onChange={(e) => handleInputChange("general_stress_levels", e.target.value)} />
                        Low
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="MEDIUM" checked={inputValues.general_stress_levels === 'MEDIUM'} onChange={(e) => handleInputChange("general_stress_levels", e.target.value)} />
                        Med
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="HIGH" checked={inputValues.general_stress_levels === 'HIGH'} onChange={(e) => handleInputChange("general_stress_levels", e.target.value)} />
                        High
                    </label>
                </div>
            </div>
        ),
        meditation_mindfulness_duration: (
            <div className="behavior-item">
                <div>Meditation Mindfulness Duration</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.meditation_mindfulness_duration} onChange={(e) => handleInputChange("meditation_mindfulness_duration", e.target.value)}></input>
                    {showError && !validInput(inputValues.meditation_mindfulness_duration, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                </div>
            </div>
        ),
        breathing_exercises_frequency: (
            <div className="behavior-item">
                <div>Breathing Exercises Frequency</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="number" placeholder="Frequency" value={inputValues.breathing_exercises_frequency} onChange={(e) => handleInputChange("breathing_exercises_frequency", e.target.value)}></input>
                </div>
            </div>
        ),
        screen_time_before_bed: (
            <div className="behavior-item">
                <div>Screen Time Before Bed</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.screen_time_before_bed} onChange={(e) => handleInputChange("screen_time_before_bed", e.target.value)}></input>
                    {showError && !validInput(inputValues.screen_time_before_bed, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                </div>
            </div>
        ),
        social_media_use_time: (
            <div className="behavior-item">
                <div>Social Media Use Time</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.social_media_use_time} onChange={(e) => handleInputChange("social_media_use_time", e.target.value)}></input>
                    {showError && !validInput(inputValues.social_media_use_time, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                </div>
            </div>
        ),
        work_hours_per_day: (
            <div className="behavior-item">
                <div>Work Hours Per Day</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.work_hours_per_day} onChange={(e) => handleInputChange("work_hours_per_day", e.target.value)}></input>
                    {showError && !validInput(inputValues.work_hours_per_day, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                </div>
            </div>
        ),
        time_spent_outdoors: (
            <div className="behavior-item">
                <div>Time Spent Outdoors</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.time_spent_outdoors} onChange={(e) => handleInputChange("time_spent_outdoors", e.target.value)}></input>
                    {showError && !validInput(inputValues.time_spent_outdoors, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                </div>
            </div>
        ),
        travel_frequency: (
            <div className="behavior-item">
                <div>Travel Frequency</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="number" placeholder="Frequency" value={inputValues.travel_frequency} onChange={(e) => handleInputChange("travel_frequency", e.target.value)}></input>
                </div>
            </div>
        ),
        altitude_exposure: (
            <div className="behavior-item">
                <div>Altitude Exposure</div>
                    <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="NONE" checked={inputValues.altitude_exposure === 'NONE'} onChange={(e) => handleInputChange("altitude_exposure", e.target.value)} />
                        None
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="LOW" checked={inputValues.altitude_exposure === 'LOW'} onChange={(e) => handleInputChange("altitude_exposure", e.target.value)} />
                        Low
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="MEDIUM" checked={inputValues.altitude_exposure === 'MEDIUM'} onChange={(e) => handleInputChange("altitude_exposure", e.target.value)} />
                        Med
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="HIGH" checked={inputValues.altitude_exposure === 'HIGH'} onChange={(e) => handleInputChange("altitude_exposure", e.target.value)} />
                        High
                    </label>
                </div>
            </div>
        ),
        time_spent_in_nature: (
            <div className="behavior-item">
                <div>Time Spent in Nature</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.time_spent_in_nature} onChange={(e) => handleInputChange("time_spent_in_nature", e.target.value)}></input>
                    {showError && !validInput(inputValues.time_spent_in_nature, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                </div>
            </div>
        ),
        cold_exposure_therapy: (
            <div className="behavior-item">
                <div>Cold Exposure Therapy</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="true" checked={inputValues.cold_exposure_therapy === true}  onChange={(e) => handleInputChange("cold_exposure_therapy", e.target.value === "true")}/>
                        True
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="false" checked={inputValues.cold_exposure_therapy === false}  onChange={(e) => handleInputChange("cold_exposure_therapy", e.target.value === "true")}/>
                        False
                    </label>
                </div>
            </div>
        ),
        sauna_heat_exposure_therapy: (
            <div className="behavior-item">
                <div>Sauna Heat Exposure Therapy</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="true" checked={inputValues.sauna_heat_exposure_therapy === true}  onChange={(e) => handleInputChange("sauna_heat_exposure_therapy", e.target.value === "true")}/>
                        True
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="false" checked={inputValues.sauna_heat_exposure_therapy === false}  onChange={(e) => handleInputChange("sauna_heat_exposure_therapy", e.target.value === "true")}/>
                        False
                    </label>
                </div>
            </div>
        ),
        sexual_activity: (
            <div className="behavior-item">
                <div>Sexual Activity</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="true" checked={inputValues.sexual_activity === true}  onChange={(e) => handleInputChange("sexual_activity", e.target.value === "true")}/>
                        True
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="false" checked={inputValues.sexual_activity === false}  onChange={(e) => handleInputChange("sexual_activity", e.target.value === "true")}/>
                        False
                    </label>
                </div>
            </div>
        ),
        time_spent_on_hobbies: (
            <div className="behavior-item">
                <div>Time Spent on Hobbies</div>
                    <div className="time-input-container">
                        <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.time_spent_on_hobbies} onChange={(e) => handleInputChange("time_spent_on_hobbies", e.target.value)}></input>
                        {showError && !validInput(inputValues.time_spent_on_hobbies, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                    </div>
            </div>
        ),
        music_exposure_duration: (
            <div className="behavior-item">
                <div>Music Exposure Duration</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.music_exposure_duration} onChange={(e) => handleInputChange("music_exposure_duration", e.target.value)}></input>
                    {showError && !validInput(inputValues.music_exposure_duration, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                </div>
            </div>
        ),
        time_spent_in_silence: (
            <div className="behavior-item">
                <div>Time Spent in Silence</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.time_spent_in_silence} onChange={(e) => handleInputChange("time_spent_in_silence", e.target.value)}></input>
                    {showError && !validInput(inputValues.time_spent_in_silence, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                </div>
            </div>
        ),
        supplements_taken: (
            <div className="behavior-item">
                <div>Supplements Taken</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="true" checked={inputValues.supplements_taken === true}  onChange={(e) => handleInputChange("supplements_taken", e.target.value === "true")}/>
                        True
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="false" checked={inputValues.supplements_taken === false}  onChange={(e) => handleInputChange("supplements_taken", e.target.value === "true")}/>
                        False
                    </label>
                </div>
            </div>
        ),
        emotional_well_being: (
            <div className="behavior-item">
                <div>Emotional Well-being</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="NONE" checked={inputValues.emotional_well_being === 'NONE'} onChange={(e) => handleInputChange("emotional_well_being", e.target.value)} />
                        None
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="LOW" checked={inputValues.emotional_well_being === 'LOW'} onChange={(e) => handleInputChange("emotional_well_being", e.target.value)} />
                        Low
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="MEDIUM" checked={inputValues.emotional_well_being === 'MEDIUM'} onChange={(e) => handleInputChange("emotional_well_being", e.target.value)} />
                        Med
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="HIGH" checked={inputValues.emotional_well_being === 'HIGH'} onChange={(e) => handleInputChange("emotional_well_being", e.target.value)} />
                        High
                    </label>
                </div>
            </div>
        ),
        time_spent_reading_learning: (
            <div className="behavior-item">
                <div>Time Spent Reading & Learning</div>
                <div className="time-input-container">
                    <input className="behavior-input time-input" type="text" placeholder="hh:mm" value={inputValues.time_spent_reading_learning} onChange={(e) => handleInputChange("time_spent_reading_learning", e.target.value)}></input>
                    {showError && !validInput(inputValues.time_spent_reading_learning, "hh:mm") && <p className="input-error-msg">Invalid input. Use hh:mm format.</p>}
                </div>
            </div>
        ),
        jetlag_travel_fatigue: (
            <div className="behavior-item">
                <div>Jetlag Travel Fatigue</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="NONE" checked={inputValues.jetlag_travel_fatigue === 'NONE'} onChange={(e) => handleInputChange("jetlag_travel_fatigue", e.target.value)} />
                        None
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="LOW" checked={inputValues.jetlag_travel_fatigue === 'LOW'} onChange={(e) => handleInputChange("jetlag_travel_fatigue", e.target.value)} />
                        Low
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="MEDIUM" checked={inputValues.jetlag_travel_fatigue === 'MEDIUM'} onChange={(e) => handleInputChange("jetlag_travel_fatigue", e.target.value)} />
                        Med
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="HIGH" checked={inputValues.jetlag_travel_fatigue === 'HIGH'} onChange={(e) => handleInputChange("jetlag_travel_fatigue", e.target.value)} />
                        High
                    </label>
                </div>
            </div>
        ),
        cold_illness: (
            <div className="behavior-item">
                <div>Cold Illness</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="true" checked={inputValues.cold_illness === true}  onChange={(e) => handleInputChange("cold_illness", e.target.value === "true")}/>
                        True
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="false" checked={inputValues.cold_illness === false}  onChange={(e) => handleInputChange("cold_illness", e.target.value === "true")}/>
                        False
                    </label>
                </div>
            </div>
        ),
        new_supplement: (
            <div className="behavior-item">
                <div>New Supplement</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="true" checked={inputValues.new_supplement === true}  onChange={(e) => handleInputChange("new_supplement", e.target.value === "true")}/>
                        True
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="false" checked={inputValues.new_supplement === false}  onChange={(e) => handleInputChange("new_supplement", e.target.value === "true")}/>
                        False
                    </label>
                </div>
            </div>
        ),
        new_medicine: (
            <div className="behavior-item">
                <div>New Medicine</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="true" checked={inputValues.new_medicine === true}  onChange={(e) => handleInputChange("new_medicine", e.target.value === "true")}/>
                        True
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="false" checked={inputValues.new_medicine === false}  onChange={(e) => handleInputChange("new_medicine", e.target.value === "true")}/>
                        False
                    </label>
                </div>
            </div>
        ),
        market_event: (
            <div className="behavior-item">
                <div>Market Event</div>
                <input className="behavior-input text-input" type="text" value={inputValues.market_event} onChange={(e) => handleInputChange("market_event", e.target.value)}></input>
            </div>
        ),
        work_stress: (
            <div className="behavior-item">
                <div>Work Stress</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="NONE" checked={inputValues.work_stress === 'NONE'} onChange={(e) => handleInputChange("work_stress", e.target.value)} />
                        None
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="LOW" checked={inputValues.work_stress === 'LOW'} onChange={(e) => handleInputChange("work_stress", e.target.value)} />
                        Low
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="MEDIUM" checked={inputValues.work_stress === 'MEDIUM'} onChange={(e) => handleInputChange("work_stress", e.target.value)} />
                        Med
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="HIGH" checked={inputValues.work_stress === 'HIGH'} onChange={(e) => handleInputChange("work_stress", e.target.value)} />
                        High
                    </label>
                </div>
            </div>
        ),
        interpersonal_stress: (
            <div className="behavior-item">
                <div>Interpersonal Stress</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="NONE" checked={inputValues.interpersonal_stress === 'NONE'} onChange={(e) => handleInputChange("interpersonal_stress", e.target.value)} />
                        None
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="LOW" checked={inputValues.interpersonal_stress === 'LOW'} onChange={(e) => handleInputChange("interpersonal_stress", e.target.value)} />
                        Low
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="MEDIUM" checked={inputValues.interpersonal_stress === 'MEDIUM'} onChange={(e) => handleInputChange("interpersonal_stress", e.target.value)} />
                        Med
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="HIGH" checked={inputValues.interpersonal_stress === 'HIGH'} onChange={(e) => handleInputChange("interpersonal_stress", e.target.value)} />
                        High
                    </label>
                </div>
            </div>
        ),
        financial_stress: (
            <div className="behavior-item">
                <div>Financial Stress</div>
                <div className="radio-container">
                    <label className="radio-label">
                        <input type="radio" value="NONE" checked={inputValues.financial_stress === 'NONE'} onChange={(e) => handleInputChange("financial_stress", e.target.value)} />
                        None
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="LOW" checked={inputValues.financial_stress === 'LOW'} onChange={(e) => handleInputChange("financial_stress", e.target.value)} />
                        Low
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="MEDIUM" checked={inputValues.financial_stress === 'MEDIUM'} onChange={(e) => handleInputChange("financial_stress", e.target.value)} />
                        Med
                    </label>
                    <label className="radio-label">
                        <input type="radio" value="HIGH" checked={inputValues.financial_stress === 'HIGH'} onChange={(e) => handleInputChange("financial_stress", e.target.value)} />
                        High
                    </label>
                </div>
            </div>
        )
    };

    const initializeValues = (pref, data) => {
        const values = {};
        pref.forEach((item) => {
            if(data[item] !== null) {
                values[item] = data[item];
            } else {
                values[item] = '';
            }
        });
        setInputValues(values);
    }

    useEffect(() => {
        const fetchSelectedPreferences = async () => {
            try {
                const [response1, response2] = await axios.all([
                    axios.get(`${url}custom-metrics-options?uuid=${userInfo.uuid}`, config),
                    axios.get(`${url}user-custom-metrics?uuid=${userInfo.uuid}&day=${behavioralTrackingDate}`, config)
                ]);
                setBehavioralTrackingData(response2.data);
                const selectedPref = Object.keys(response1.data).filter((item) => (response1.data[item] === true));
                initializeValues(selectedPref, response2.data);
                setSelectedPreferences(selectedPref);
                setSelectedItems(selectedPref);
                if(!selectedPref.length) {
                    setShowPreferences(true);
                }
            } catch (err) {
                console.log(err);
            } finally {
                setLoading(false);
            }
        }

        fetchSelectedPreferences();
    }, [])

    const handleSelect = (item) => {
        if (selectedItems.includes(item)) {
          setSelectedItems(selectedItems.filter((selected) => selected !== item));
        } else if (selectedItems.length < 6) {
          setSelectedItems([...selectedItems, item]);
        }
    };

    const updateCustomMetricsOptions = async () => {
        const payload = customMetricOptionsPayload;
        payload.email = userInfo.email;
        selectedItems.forEach((item) => {
            payload[item] = true;
        });
        try {
            const response = await axios.put(`${url}custom-metrics-options`, payload, config);
            const selectedPref = Object.keys(response.data).filter((item) => (response.data[item] === true));
            setSelectedPreferences(selectedPref);
            initializeValues(selectedPref, behavioralTrackingData);
            setShowPreferences(false);
            setUpdatePref(false);
        } catch (err) {
            console.log(err); 
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if(selectedItems.length === 6) {
            setShowMinPreferencesError(false);
        }
    }, [selectedItems]);

    const savePreferences = () => {
        if(selectedItems.length < 6) {
            setShowMinPreferencesError(true);
        } else {
            setLoading(true);
            updateCustomMetricsOptions();
        }
    }

    const fetchCustomMetricValues = async (date) => {
        setLoading(true);
        try {
            const response = await axios.get(`${url}user-custom-metrics?uuid=${userInfo.uuid}&day=${date}`, config);
            setBehavioralTrackingData(response.data);
            initializeValues(selectedPreferences, response.data);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    const handleDateChange = (e) => {
        setBehavioralTrackingDate(e.target.value);
        fetchCustomMetricValues(e.target.value);
    }

    const saveCustomMetricValues = async () => {
        const payload = customMetricValuesPayload;
        payload.email = userInfo.email;
        payload.day = behavioralTrackingDate;
        selectedPreferences.forEach((item) => {
            let val = inputValues[item];
            if(inputValues[item] === undefined || inputValues[item] === '' ) {
                val = null;
            }
            payload[item] = val;
        });
        try {
            const response = await axios.put(`${url}user-custom-metrics`, payload, config);
            setBehavioralTrackingData(response.data);
            setHasChanges(false);
            initializeValues(selectedPreferences, response.data);
        } catch (err) {
            console.log(err); 
        } finally {
            setLoading(false);
        }
    }

    const saveBehavioralTrackingValues = () => {
        let error = false;
        selectedPreferences.forEach((item) => {
            if(item === "sunlight_exposure_duration" || item === "time_with_close_family_friends" || item === "extroverted_time" || item === "introverted_alone_time" || item === "nap_duration" || item === "meditation_mindfulness_duration" || item === "screen_time_before_bed" || item === "social_media_use_time" || item === "work_hours_per_day" || item === "time_spent_in_nature" || item === "time_spent_on_hobbies" || item === "music_exposure_duration" || item === "time_spent_in_silence" || item === "time_spent_reading_learning" || item === "time_spent_outdoors") {
                if(!validInput(inputValues[item], "hh:mm")) {
                    error = true;
                }
            } else if(item === "sunlight_exposure_timing") {
                if(!validInput(inputValues[item], "hh:mm:ss")) {
                    error = true;
                }
            } 
        });
        setShowError(error);
        if(!error) {
            setLoading(true);
            saveCustomMetricValues();
        }
    }

    const cancelEdit = () => {
        initializeValues(selectedPreferences, behavioralTrackingData);
        setHasChanges(false);
        setShowError(false);
    }

    const updatePreferences = () => {
        setShowPreferences(true);
        setUpdatePref(true);
        cancelEdit();
    }

    const cancelUpdatePref = () => {
        setShowPreferences(false);
        setUpdatePref(false);
        setSelectedItems(selectedPreferences);
    }

    const handleInputChange = (behavior, value) => {
        setInputValues((prevValues) => ({
          ...prevValues,
          [behavior]: value,
        }));
        setHasChanges(true);
    };

    return (
        <div className="container">
            <div className="main-content">
                <section className="card">
                    {loading &&
                        <div className="spinner-container">
                            <ClipLoader color={"#123abc"} loading={loading} size={50} />
                        </div>
                    }
                    {showPreferences && !loading &&
                        <div className="preferences-section">
                            <p>Please select your preferences (6):</p>
                            <div className="accordion-container">
                                {Object.entries(data).map(([category, items], index) => (
                                    <div className="accordion-item" key={`accordion-item-${index}`}>
                                        <div
                                            className="accordion-header"
                                            onClick={() => toggleAccordion(index)}
                                        >
                                            {category}
                                            <span className={`arrow ${openSections.includes(index) ? 'open' : ''}`}>&#9660;</span>
                                        </div>
                                        {openSections.includes(index) && (
                                            <div className="accordion-body">
                                            <ul className="preferences-list">
                                                {items.map((item, idx) => (
                                                    <li 
                                                        key={`accordion-item-${index}-${idx}`}
                                                        onClick={() => handleSelect(item)}
                                                        className="preference-item"
                                                        style={{
                                                            backgroundColor: selectedItems.includes(item) ? 'rgb(223, 243, 245)' : '#f8f9fa',
                                                            border: selectedItems.includes(item) ? 'none' : '1px solid #ced4da',
                                                            pointerEvents: selectedItems.length === 6 && !selectedItems.includes(item) ? 'none' : 'auto',
                                                            opacity: selectedItems.length === 6 && !selectedItems.includes(item) ? 0.5 : 1,
                                                        }}
                                                    >
                                                        {item.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
                                                    </li>
                                                ))}
                                            </ul>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="button-container">
                                <button
                                    className="submit-button"
                                    onClick={savePreferences}
                                >
                                    {updatePref ? 'Update' : 'Submit'}
                                </button>
                                {updatePref &&
                                    <button className="cancel-btn" onClick={cancelUpdatePref}>Cancel</button>
                                }
                                {showMinPreferencesError && 
                                    <div className="error-msg">Please select at least 6 preferences</div>
                                }
                            </div>
                        </div>
                    }
                    {!showPreferences && !loading &&
                        <div className="preferences-section behavioral-tracking">
                            <button className="update-btn" onClick={updatePreferences}>Update Preferences</button>
                            <div>
                                <label className="input-label" htmlFor="behavioralTrackingDate">Start Date:</label>
                                <input className="date-input" type="date" id="behavioralTrackingDate" name="behavioralTrackingDate" value={behavioralTrackingDate} onChange={handleDateChange}></input>
                            </div>

                            <div className="behavior-list">
                                {selectedPreferences.map((item, index) => (
                                    <div key={`behavior-item-${index}`}>
                                        {behaviorComponents[item]}
                                    </div>
                                ))}
                            </div>
                            {hasChanges &&
                                <div>
                                    <button className="action-btn save-btn" onClick={saveBehavioralTrackingValues}>Save</button>
                                    <button className="action-btn cancel-btn" onClick={cancelEdit} >Reset</button>
                                </div>
                            }
                        </div>
                    }
                </section>
            </div>
        </div>
    );
}

export default BehavioralTracking;